import { VentesAddons } from './pages/VentesAddons.js';
import { Addons } from './pages/Addons.js';
import type { JSX } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router';
import type { Action } from '@artosera/shared/permissions';
import { RequireAction } from './auth/RequireAction.js';
import { RequireConsoleAuth } from './auth/RequireConsoleAuth.js';
import { ConsoleShell } from './components/shell/ConsoleShell.js';
import { Associes } from './pages/Associes.js';
import { Comptabilite } from './pages/Comptabilite/Comptabilite.js';
import { AuditGlobal } from './pages/AuditGlobal.js';
import { DemandeOuvertureDetail } from './pages/DemandesOuverture/DemandeOuvertureDetail.js';
import { DemandesOuvertureList } from './pages/DemandesOuverture/DemandesOuvertureList.js';
import { DemandesSite } from './pages/DemandesSite.js';
import { DiagnosticsIos } from './pages/DiagnosticsIos.js';
import { Finances } from './pages/Finances/Finances.js';
import { GalleriesList } from './pages/GalleriesList.js';
import { GalleryDetail } from './pages/GalleryDetail.js';
import { Health } from './pages/Health.js';
import { Interrupteurs } from './pages/Interrupteurs.js';
import { JobsGlobal } from './pages/JobsGlobal.js';
import { Jalons } from './pages/Jalons.js';
import { Login } from './pages/Login.js';
import { Assistant } from './pages/Ouvertures/Assistant.js';
import { DossierDetail } from './pages/Ouvertures/DossierDetail.js';
import { OuverturesList } from './pages/Ouvertures/OuverturesList.js';
import { Paliers } from './pages/Paliers.js';
import { AppsMobiles } from './pages/AppsMobiles.js';
import { ProfilsBibliotheque } from './pages/ProfilsBibliotheque.js';
import { Securite } from './pages/Securite.js';
import { VueDAigle } from './pages/VueDAigle/VueDAigle.js';

/**
 * Une route de la console : session établie, puis le droit que la matrice
 * exige. Sans droit, retour à la vue d'aigle — c'est ainsi que l'associé,
 * qui n'a que `console:read-overview`, reste confiné à cette page.
 */
function Protege({ enfant, action }: { enfant: JSX.Element; action: Action }): JSX.Element {
  return (
    <RequireConsoleAuth>
      <RequireAction action={action}>
        <ConsoleShell>{enfant}</ConsoleShell>
      </RequireAction>
    </RequireConsoleAuth>
  );
}

/**
 * `/` et toute route inconnue mènent à la vue d'aigle. La requête suit : le
 * mode `?demo&role=…` se lit dans l'URL, et `/` est la première adresse qu'on
 * ouvre.
 */
function VersLAccueil(): JSX.Element {
  const { search } = useLocation();
  return <Navigate to={{ pathname: '/vue-d-aigle', search }} replace />;
}

/** `/galeries/nouvelle` mène à l'assistant ; la requête (`?demo`) suit. */
function VersAssistant(): JSX.Element {
  const { search } = useLocation();
  return <Navigate to={{ pathname: '/ouvertures/nouvelle', search }} replace />;
}

/** Les écrans d'exploitation (galeries, travaux, journal, santé…) : la lecture de la console. */
function Exploitation({ enfant }: { enfant: JSX.Element }): JSX.Element {
  return <Protege action="console:read" enfant={enfant} />;
}

export default function App(): JSX.Element {
  return (
    <Routes>
      <Route path="/connexion" element={<Login />} />
      <Route path="/" element={<VersLAccueil />} />
      <Route
        path="/vue-d-aigle"
        element={<Protege action="console:read-overview" enfant={<VueDAigle />} />}
      />
      {/* La comptabilité de la vue d'aigle : la direction, et les associés pour leur part. */}
      <Route
        path="/comptabilite"
        element={<Protege action="console:read-accounting" enfant={<Comptabilite />} />}
      />
      {/* La sécurité du compte : tout rôle de console, associé compris, et le
          seul écran d'une session confinée à l'enrôlement 2FA. Aucune action
          de la matrice : c'est le compte, pas la plateforme. */}
      <Route
        path="/securite"
        element={
          <RequireConsoleAuth>
            <ConsoleShell>
              <Securite />
            </ConsoleShell>
          </RequireConsoleAuth>
        }
      />
      {/* Saisies financières : l'exploitation et la direction (spec § 6). */}
      <Route
        path="/finances"
        element={<Protege action="console:manage-subscriptions" enfant={<Finances />} />}
      />
      <Route
        path="/jalons"
        element={<Protege action="console:manage-milestones" enfant={<Jalons />} />}
      />
      <Route
        path="/associes"
        element={<Protege action="console:manage-associates" enfant={<Associes />} />}
      />
      <Route path="/galeries" element={<Exploitation enfant={<GalleriesList />} />} />
      {/* L'ancien formulaire est remplacé par l'assistant d'ouverture (ARTO-128). */}
      <Route path="/galeries/nouvelle" element={<Exploitation enfant={<VersAssistant />} />} />
      <Route path="/galeries/:id" element={<Exploitation enfant={<GalleryDetail />} />} />
      <Route path="/galeries/:id/:onglet" element={<Exploitation enfant={<GalleryDetail />} />} />
      {/* Les demandes d'ouverture venues d'artosera.com. `/demandes/:id` est le chemin
          du courriel d'alerte envoyé par l'API : il ne change pas. */}
      <Route
        path="/demandes"
        element={<Protege action="console:read-onboarding" enfant={<DemandesOuvertureList />} />}
      />
      <Route
        path="/demandes/:id"
        element={<Protege action="console:read-onboarding" enfant={<DemandeOuvertureDetail />} />}
      />
      <Route
        path="/ouvertures"
        element={<Protege action="console:read-onboarding" enfant={<OuverturesList />} />}
      />
      <Route
        path="/ouvertures/nouvelle"
        element={<Protege action="console:manage-onboarding" enfant={<Assistant />} />}
      />
      <Route
        path="/ouvertures/:id/assistant"
        element={<Protege action="console:read-onboarding" enfant={<Assistant />} />}
      />
      <Route
        path="/ouvertures/:id"
        element={<Protege action="console:read-onboarding" enfant={<DossierDetail />} />}
      />
      <Route
        path="/ouvertures/:id/:onglet"
        element={<Protege action="console:read-onboarding" enfant={<DossierDetail />} />}
      />
      {/* La bibliothèque des profils de correspondance : lecture ; l'édition se fait depuis un lot. */}
      <Route
        path="/reprise/profils"
        element={
          <Protege action="console:manage-import-profiles" enfant={<ProfilsBibliotheque />} />
        }
      />
      {/* Les demandes de site des galeries : lecture pour l'exploitation, gestes selon le rôle. */}
      <Route path="/demandes-site" element={<Exploitation enfant={<DemandesSite />} />} />
      <Route path="/diagnostics-ios" element={<Exploitation enfant={<DiagnosticsIos />} />} />
      <Route path="/travaux" element={<Exploitation enfant={<JobsGlobal />} />} />
      <Route path="/journal" element={<Exploitation enfant={<AuditGlobal />} />} />
      <Route path="/sante" element={<Exploitation enfant={<Health />} />} />
      <Route path="/interrupteurs" element={<Exploitation enfant={<Interrupteurs />} />} />
      {/* Lecture pour tout rôle d'exploitation ; l'écriture se règle dans la page. */}
      <Route
        path="/addons/ventes"
        element={<Protege action="console:read-addon-sales" enfant={<VentesAddons />} />}
      />
      <Route path="/addons" element={<Exploitation enfant={<Addons />} />} />
      <Route path="/paliers" element={<Exploitation enfant={<Paliers />} />} />
      {/* Lecture pour tout rôle d'exploitation ; le réglage se contrôle dans la page. */}
      <Route path="/apps-mobiles" element={<Exploitation enfant={<AppsMobiles />} />} />
      <Route path="*" element={<VersLAccueil />} />
    </Routes>
  );
}
